import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { SEC, severityColor, statusColor } from './SecurityTheme';
import {
  getAllSecurityAlerts,
  updateSecurityAlert,
  getSecurityFindings,
  releaseQuarantine,
  quarantineUser,
  updateSecurityFinding,
  disableInboxRule,
  SecurityAlertItem,
  SecurityAlertsResponse,
  SecurityFinding,
  QuarantineEvent,
} from '../../services/api';
import { format, parseISO } from 'date-fns';
import {
  Shield,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Search,
  Filter,
  CheckCircle2,
  Eye,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Unlock,
  ArrowRight,
  Mail,
  Trash2,
  BookOpen,
  FolderOpen,
  ShieldBan,
  ShieldCheck,
  XCircle,
  User,
  Users,
} from 'lucide-react';
import AlertNotificationSettings from './AlertNotificationSettings';

const STATUS_OPTIONS = ['all', 'new', 'acknowledged', 'investigating', 'resolved', 'false_positive'];
const SEVERITY_OPTIONS = ['all', 'critical', 'warning', 'info'];

const IOC_TYPE_LABELS: Record<string, string> = {
  suspicious_inbox_rule: 'Suspicious Inbox Rule',
  foreign_sign_in: 'Foreign Sign-In',
  legacy_auth: 'Legacy Auth Protocol',
  risky_sign_in: 'Risky Sign-In',
  external_forwarding: 'External Forwarding',
  no_mfa: 'No MFA Configured',
  password_reset: 'Password/MFA Reset',
  known_attacker_ip: 'Known Attacker IP',
  attacker_sent_email: 'Attacker Sent Email',
  oauth_app_consent: 'OAuth App Consent',
  mass_rule_cleanup: 'Mass Rule Cleanup',
  cross_user_ip_correlation: 'Shared IP Activity',
  privileged_role: 'Privileged Admin Role',
  admin_role_change: 'Admin Role Change',
  conditional_access_change: 'CA Policy Change',
  suspicious_mfa_registration: 'Suspicious MFA Registration',
  mailbox_delegation_change: 'Mailbox Delegation Change',
  license_change: 'License Change',
  app_registration: 'App Registration',
};

function iocTypeLabel(type: string): string {
  return IOC_TYPE_LABELS[type] || type.replace(/_/g, ' ');
}

export default function SecurityAlertsPage() {
  const [alertsData, setAlertsData] = useState<SecurityAlertsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState('new');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Expanded row
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Action loading
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchAlerts = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const params: Record<string, any> = { limit: 200 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (severityFilter !== 'all') params.severity = severityFilter;
      const data = await getAllSecurityAlerts(params);
      if (data.success) setAlertsData(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load alerts');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [statusFilter, severityFilter]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => fetchAlerts(), 30000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  const handleAction = async (alertId: string, clientId: string, status: string) => {
    setActionLoading(alertId);
    try {
      await updateSecurityAlert(alertId, clientId, { status });
      // Refresh the list
      await fetchAlerts();
    } catch (err: any) {
      console.error('Failed to update alert:', err);
    } finally {
      setActionLoading(null);
    }
  };

  // Filter alerts by search
  const filteredAlerts = (alertsData?.alerts || []).filter((a) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      a.title.toLowerCase().includes(q) ||
      a.summary.toLowerCase().includes(q) ||
      a.clientName.toLowerCase().includes(q) ||
      a.iocTypes.some(t => iocTypeLabel(t).toLowerCase().includes(q))
    );
  });

  const severityBadge = (severity: string) => {
    const color = severityColor(severity);
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider"
        style={{ background: `${color}20`, color, border: `1px solid ${color}40` }}
      >
        {severity === 'critical' && <AlertTriangle className="h-3 w-3 mr-1" />}
        {severity}
      </span>
    );
  };

  const statusBadge = (status: string) => {
    const color = statusColor(status);
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium"
        style={{ color, background: `${color}15`, border: `1px solid ${color}30` }}
      >
        <span
          className="inline-block w-1.5 h-1.5 rounded-full mr-1.5"
          style={{ background: color, boxShadow: status === 'new' ? `0 0 4px ${color}` : 'none' }}
        />
        {status.replace('_', ' ')}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: SEC.bg }}>
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-10 w-10 border-2 mx-auto mb-4"
            style={{ borderColor: SEC.border, borderTopColor: SEC.cyan }}
          />
          <p className="text-sm font-mono tracking-wider" style={{ color: SEC.t3 }}>
            LOADING ALERTS...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: SEC.bg }}>
      {/* Header */}
      <div
        className="sticky top-0 z-10 px-6 py-3 flex items-center justify-between"
        style={{
          background: SEC.header,
          borderBottom: `1px solid ${SEC.border}`,
          boxShadow: `0 1px 8px rgba(0,0,0,0.4)`,
        }}
      >
        <div className="flex items-center gap-3">
          <Link to="/security" className="p-1 rounded hover:bg-white/5 transition-colors">
            <ChevronLeft className="h-5 w-5" style={{ color: SEC.t2 }} />
          </Link>
          <Shield className="h-5 w-5" style={{ color: SEC.cyan }} />
          <h1
            className="text-sm font-bold tracking-[0.2em] uppercase"
            style={{ color: SEC.t1 }}
          >
            Alerts Console
          </h1>
          {alertsData && (
            <span className="text-xs font-mono" style={{ color: SEC.t3 }}>
              {alertsData.total} total
            </span>
          )}
        </div>
        <button
          onClick={() => fetchAlerts(true)}
          disabled={refreshing}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded transition-colors"
          style={{ color: SEC.cyan, border: `1px solid ${SEC.cyanDim}` }}
        >
          <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Summary bar */}
      {alertsData && (
        <div
          className="px-6 py-2.5 flex items-center gap-6"
          style={{ background: SEC.panel2, borderBottom: `1px solid ${SEC.border}` }}
        >
          <span className="text-[10px] font-medium uppercase tracking-wider" style={{ color: SEC.t3 }}>
            By Status:
          </span>
          {Object.entries(alertsData.byStatus).map(([status, count]) => {
            const c = statusColor(status);
            return (
              <button
                key={status}
                onClick={() => setStatusFilter(statusFilter === status ? 'all' : status)}
                className={`text-xs font-mono px-2 py-0.5 rounded transition-colors ${
                  statusFilter === status ? 'ring-1' : ''
                }`}
                style={{
                  color: c,
                  background: statusFilter === status ? `${c}15` : 'transparent',
                  outline: statusFilter === status ? `1px solid ${c}` : 'none',
                }}
              >
                {status.replace('_', ' ')}: {count}
              </button>
            );
          })}
          <div className="flex-1" />
          <span className="text-[10px] font-medium uppercase tracking-wider" style={{ color: SEC.t3 }}>
            By Severity:
          </span>
          {Object.entries(alertsData.bySeverity).map(([sev, count]) => {
            const c = severityColor(sev);
            return (
              <button
                key={sev}
                onClick={() => setSeverityFilter(severityFilter === sev ? 'all' : sev)}
                className={`text-xs font-mono px-2 py-0.5 rounded transition-colors ${
                  severityFilter === sev ? 'ring-1' : ''
                }`}
                style={{
                  color: c,
                  background: severityFilter === sev ? `${c}15` : 'transparent',
                  outline: severityFilter === sev ? `1px solid ${c}` : 'none',
                }}
              >
                {sev}: {count}
              </button>
            );
          })}
        </div>
      )}

      {/* Notification Settings */}
      <AlertNotificationSettings />

      {/* Filter Controls */}
      <div
        className="px-6 py-3 flex items-center gap-4"
        style={{ borderBottom: `1px solid ${SEC.border}` }}
      >
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: SEC.t3 }} />
          <input
            type="text"
            placeholder="Search alerts by title, client, or IOC type..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 rounded text-sm outline-none"
            style={{
              background: SEC.panel,
              border: `1px solid ${SEC.border}`,
              color: SEC.t1,
            }}
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4" style={{ color: SEC.t3 }} />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-xs py-2 px-3 rounded outline-none cursor-pointer"
            style={{ background: SEC.panel, border: `1px solid ${SEC.border}`, color: SEC.t1 }}
          >
            {STATUS_OPTIONS.map(s => (
              <option key={s} value={s}>{s === 'all' ? 'All Statuses' : s.replace('_', ' ')}</option>
            ))}
          </select>
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
            className="text-xs py-2 px-3 rounded outline-none cursor-pointer"
            style={{ background: SEC.panel, border: `1px solid ${SEC.border}`, color: SEC.t1 }}
          >
            {SEVERITY_OPTIONS.map(s => (
              <option key={s} value={s}>{s === 'all' ? 'All Severities' : s}</option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div
          className="mx-6 mt-4 px-4 py-3 rounded text-sm"
          style={{ background: `${SEC.red}10`, color: SEC.red, border: `1px solid ${SEC.red}30` }}
        >
          {error}
          <button
            onClick={() => fetchAlerts()}
            className="ml-3 underline text-xs"
          >
            Retry
          </button>
        </div>
      )}

      {/* Alerts Table */}
      <div className="px-6 py-4">
        {filteredAlerts.length === 0 ? (
          <div className="text-center py-16">
            <Shield className="h-12 w-12 mx-auto mb-3" style={{ color: SEC.t3 }} />
            <p className="text-sm" style={{ color: SEC.t2 }}>
              {searchQuery ? 'No alerts matching your search' : 'No security alerts'}
            </p>
            <p className="text-xs mt-1" style={{ color: SEC.t4 }}>
              {searchQuery ? 'Try different search terms' : 'Run a collection to generate alerts'}
            </p>
          </div>
        ) : (
          <div
            className="rounded-lg overflow-hidden"
            style={{ border: `1px solid ${SEC.border}` }}
          >
            <table className="w-full text-xs">
              <thead>
                <tr style={{ background: SEC.panel2, borderBottom: `1px solid ${SEC.border}` }}>
                  <th className="text-left px-4 py-2.5 font-medium uppercase tracking-wider" style={{ color: SEC.t3, fontSize: '10px' }}>Severity</th>
                  <th className="text-left px-4 py-2.5 font-medium uppercase tracking-wider" style={{ color: SEC.t3, fontSize: '10px' }}>Client</th>
                  <th className="text-left px-4 py-2.5 font-medium uppercase tracking-wider" style={{ color: SEC.t3, fontSize: '10px' }}>Type</th>
                  <th className="text-left px-4 py-2.5 font-medium uppercase tracking-wider" style={{ color: SEC.t3, fontSize: '10px' }}>Summary</th>
                  <th className="text-left px-4 py-2.5 font-medium uppercase tracking-wider" style={{ color: SEC.t3, fontSize: '10px' }}>Status</th>
                  <th className="text-right px-4 py-2.5 font-medium uppercase tracking-wider" style={{ color: SEC.t3, fontSize: '10px' }}>Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredAlerts.map((alert) => {
                  const isExpanded = expandedId === alert.id;
                  return (
                    <AlertRow
                      key={alert.id}
                      alert={alert}
                      isExpanded={isExpanded}
                      onToggle={() => setExpandedId(isExpanded ? null : alert.id)}
                      onAction={handleAction}
                      actionLoading={actionLoading === alert.id}
                      severityBadge={severityBadge}
                      statusBadge={statusBadge}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

interface AlertRowProps {
  alert: SecurityAlertItem;
  isExpanded: boolean;
  onToggle: () => void;
  onAction: (alertId: string, clientId: string, status: string) => void;
  actionLoading: boolean;
  severityBadge: (s: string) => JSX.Element;
  statusBadge: (s: string) => JSX.Element;
}

function AlertRow({ alert, isExpanded, onToggle, onAction, actionLoading, severityBadge, statusBadge }: AlertRowProps) {
  // Quarantine release state
  const [releasingUser, setReleasingUser] = useState<string | null>(null);
  const [releaseResults, setReleaseResults] = useState<Record<string, 'success' | 'error'>>({});
  const [localQuarantineActions, setLocalQuarantineActions] = useState<QuarantineEvent[]>(alert.quarantineActions || []);
  const [confirmRelease, setConfirmRelease] = useState<string | null>(null); // userId to confirm
  const [releasingAll, setReleasingAll] = useState(false);

  // Manual quarantine state
  const [quarantiningUser, setQuarantiningUser] = useState<string | null>(null);
  const [quarantineResults, setQuarantineResults] = useState<Record<string, 'success' | 'error'>>({});
  const [confirmQuarantine, setConfirmQuarantine] = useState<string | null>(null);

  // Finding dismiss state
  const [dismissingFinding, setDismissingFinding] = useState<string | null>(null);
  const [dismissedFindings, setDismissedFindings] = useState<Set<string>>(new Set());
  const [undismissingFinding, setUndismissingFinding] = useState<string | null>(null);

  // User accordion state
  const [expandedUser, setExpandedUser] = useState<string | null>(null);

  // Findings detail state
  const [findings, setFindings] = useState<SecurityFinding[] | null>(null);
  const [findingsLoading, setFindingsLoading] = useState(false);
  const [findingsError, setFindingsError] = useState<string | null>(null);

  // Fetch findings when expanded
  useEffect(() => {
    if (isExpanded && !findings && !findingsLoading && alert.findingIds.length > 0) {
      setFindingsLoading(true);
      getSecurityFindings(alert.clientId, { limit: 500 })
        .then((res) => {
          const alertFindingIds = new Set(alert.findingIds);
          const matched = res.findings.filter(f => alertFindingIds.has(f.id));
          setFindings(matched);
        })
        .catch((err) => setFindingsError(err.message || 'Failed to load findings'))
        .finally(() => setFindingsLoading(false));
    }
  }, [isExpanded, findings, findingsLoading, alert.findingIds, alert.clientId]);

  const handleReleaseUser = async (userId: string) => {
    setConfirmRelease(null);
    setReleasingUser(userId);
    try {
      const res = await releaseQuarantine({
        clientId: alert.clientId,
        userId,
        alertId: alert.id,
        notes: `Quarantine released from Alerts Console`,
      });
      if (res.success) {
        setReleaseResults(prev => ({ ...prev, [userId]: 'success' }));
        setLocalQuarantineActions(prev =>
          prev.map(qa => qa.userId === userId ? { ...qa, reversed: true, reversedAt: new Date().toISOString() } : qa)
        );
      } else {
        setReleaseResults(prev => ({ ...prev, [userId]: 'error' }));
      }
    } catch {
      setReleaseResults(prev => ({ ...prev, [userId]: 'error' }));
    } finally {
      setReleasingUser(null);
    }
  };

  const handleReleaseAll = async () => {
    setReleasingAll(true);
    const unreleased = getQuarantinedUsers();
    for (const userId of unreleased) {
      await handleReleaseUser(userId);
    }
    setReleasingAll(false);
  };

  const getQuarantinedUsers = (): string[] => {
    const users = new Map<string, boolean>();
    for (const qa of localQuarantineActions) {
      if (qa.action === 'block_sign_in' && qa.success && !qa.reversed && !releaseResults[qa.userId]) {
        users.set(qa.userId, true);
      }
    }
    return Array.from(users.keys());
  };

  const handleQuarantineUser = async (userId: string) => {
    setConfirmQuarantine(null);
    setQuarantiningUser(userId);
    try {
      const res = await quarantineUser({
        clientId: alert.clientId,
        userId,
        reason: `Manual quarantine from alert ${alert.id}`,
      });
      if (res.success) {
        setQuarantineResults(prev => ({ ...prev, [userId]: 'success' }));
      } else {
        setQuarantineResults(prev => ({ ...prev, [userId]: 'error' }));
      }
    } catch {
      setQuarantineResults(prev => ({ ...prev, [userId]: 'error' }));
    } finally {
      setQuarantiningUser(null);
    }
  };

  const handleDismissFinding = async (findingId: string) => {
    setDismissingFinding(findingId);
    try {
      await updateSecurityFinding(findingId, {
        clientId: alert.clientId,
        status: 'false_positive',
        notes: 'Dismissed as normal rule from Alerts Console',
      });
      setDismissedFindings(prev => new Set(prev).add(findingId));
    } catch {
      // silently fail — user can retry
    } finally {
      setDismissingFinding(null);
    }
  };

  // Auto-resolve: when ALL findings in the alert are dismissed, resolve automatically.
  // Must check against alert.findingIds (the complete list), not just the loaded
  // findings array (which may be an incomplete subset from Cosmos).
  useEffect(() => {
    if (
      dismissedFindings.size > 0 &&
      alert.findingIds.length > 0 &&
      alert.status !== 'resolved' && alert.status !== 'false_positive'
    ) {
      // Build set of all finding IDs that are accounted for (dismissed this session OR already false_positive in Cosmos)
      const accountedFor = new Set(dismissedFindings);
      if (findings) {
        for (const f of findings) {
          if (f.status === 'false_positive') accountedFor.add(f.id);
        }
      }
      // Only auto-resolve if EVERY finding ID in the alert is accounted for
      if (alert.findingIds.every(id => accountedFor.has(id))) {
        onAction(alert.id, alert.clientId, 'false_positive');
      }
    }
  }, [dismissedFindings, findings, alert.status, alert.findingIds, alert.id, alert.clientId, onAction]);

  const handleUndismissFinding = async (findingId: string) => {
    setUndismissingFinding(findingId);
    try {
      await updateSecurityFinding(findingId, {
        clientId: alert.clientId,
        status: 'new',
        notes: 'Unmarked as safe from Alerts Console',
      });
      setDismissedFindings(prev => {
        const next = new Set(prev);
        next.delete(findingId);
        return next;
      });
    } catch {
      // silently fail — user can retry
    } finally {
      setUndismissingFinding(null);
    }
  };

  const quarantinedUsers = alert.autoQuarantineApplied ? getQuarantinedUsers() : [];

  // Group findings by user for accordion
  const userSummaries = useMemo(() => {
    if (!findings || findings.length === 0) return [];
    const groups = groupFindingsByUser(findings);
    return groups.map(([userId, userFindings]) => {
      const allDismissed = userFindings.every(f => dismissedFindings.has(f.id) || f.status === 'false_positive');
      const isQuarantined = quarantineResults[userId] === 'success';
      const isAlreadyBlocked = localQuarantineActions.some(qa => qa.userId === userId && qa.action === 'block_sign_in' && qa.success && !qa.reversed);
      const highestSeverity = userFindings.some(f => f.severity === 'critical') ? 'critical' : userFindings.some(f => f.severity === 'warning') ? 'warning' : 'info';
      return { userId, userFindings, allDismissed, isQuarantined, isAlreadyBlocked, highestSeverity };
    });
  }, [findings, dismissedFindings, quarantineResults, localQuarantineActions]);

  return (
    <>
      <tr
        className="cursor-pointer transition-colors hover:bg-white/[0.02]"
        style={{ borderBottom: `1px solid ${SEC.border}`, background: isExpanded ? SEC.panel2 : SEC.panel }}
        onClick={onToggle}
      >
        <td className="px-4 py-2.5">
          {severityBadge(alert.severity)}
        </td>
        <td className="px-4 py-2.5">
          <Link
            to={`/clients/${alert.clientId}`}
            className="font-medium hover:underline"
            style={{ color: SEC.t1 }}
            onClick={(e) => e.stopPropagation()}
          >
            {alert.clientName}
          </Link>
          <p className="text-[10px] mt-0.5" style={{ color: SEC.t4 }}>{alert.tenantDomain}</p>
        </td>
        <td className="px-4 py-2.5">
          <div className="flex flex-wrap gap-1">
            {alert.iocTypes.slice(0, 2).map((ioc, i) => (
              <span
                key={i}
                className="text-[10px] px-1.5 py-0.5 rounded font-medium"
                style={{ background: SEC.cyanGlow, color: SEC.cyanDim }}
              >
                {iocTypeLabel(ioc)}
              </span>
            ))}
            {alert.iocTypes.length > 2 && (
              <span className="text-[9px]" style={{ color: SEC.t4 }}>+{alert.iocTypes.length - 2}</span>
            )}
          </div>
        </td>
        <td className="px-4 py-2.5">
          <p className="truncate max-w-[300px]" style={{ color: SEC.t1 }}>{alert.title}</p>
        </td>
        <td className="px-4 py-2.5">
          {statusBadge(alert.status)}
        </td>
        <td className="px-4 py-2.5 text-right">
          <div className="flex items-center justify-end gap-1">
            <span className="font-mono" style={{ color: SEC.t3 }}>
              {formatDate(alert.createdDate)}
            </span>
            {isExpanded ? (
              <ChevronUp className="h-3 w-3" style={{ color: SEC.t3 }} />
            ) : (
              <ChevronDown className="h-3 w-3" style={{ color: SEC.t3 }} />
            )}
          </div>
        </td>
      </tr>
      {isExpanded && (
        <tr style={{ background: SEC.panel2 }}>
          <td colSpan={6} className="px-4 py-4">
            <div className="space-y-3">
              {/* Summary */}
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wider mb-1" style={{ color: SEC.t3 }}>
                  Summary
                </p>
                <p className="text-sm leading-relaxed" style={{ color: SEC.t2 }}>
                  {alert.summary || 'No summary available'}
                </p>
              </div>

              {/* User Accordion */}
              {alert.findingIds.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5" style={{ color: SEC.cyan }}>
                      <Users className="h-3.5 w-3.5" />
                      Affected Users ({alert.affectedUsers.length})
                    </p>
                    {quarantinedUsers.length >= 2 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleReleaseAll(); }}
                        disabled={releasingAll || !!releasingUser}
                        className="flex items-center gap-1 text-[10px] px-2 py-1 rounded transition-colors"
                        style={{ color: SEC.green, border: `1px solid ${SEC.green}40`, background: `${SEC.green}10` }}
                      >
                        {releasingAll ? <Loader2 className="h-3 w-3 animate-spin" /> : <Unlock className="h-3 w-3" />}
                        Release All ({quarantinedUsers.length})
                      </button>
                    )}
                  </div>
                  {findingsLoading && (
                    <div className="flex items-center gap-2 text-xs py-3" style={{ color: SEC.t3 }}>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Loading findings...
                    </div>
                  )}
                  {findingsError && (
                    <p className="text-xs py-1" style={{ color: SEC.red }}>{findingsError}</p>
                  )}
                  {userSummaries.length > 0 && (
                    <div className="rounded-lg overflow-hidden" style={{ border: `1px solid ${SEC.border}` }}>
                      {userSummaries.map(({ userId, userFindings, allDismissed, isQuarantined, isAlreadyBlocked, highestSeverity }) => {
                        const isUserExpanded = expandedUser === userId;
                        const sevColor = severityColor(highestSeverity);
                        const isReleased = releaseResults[userId] === 'success';
                        const releaseFailed = releaseResults[userId] === 'error';
                        const isReleasing = releasingUser === userId;

                        return (
                          <div key={userId} style={{ borderBottom: `1px solid ${SEC.border}` }}>
                            {/* User collapsed row */}
                            <div
                              className="flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors hover:bg-white/[0.03]"
                              style={{ background: isUserExpanded ? SEC.bg : SEC.panel }}
                              onClick={() => setExpandedUser(isUserExpanded ? null : userId)}
                            >
                              <ChevronRight
                                className="h-3.5 w-3.5 transition-transform flex-shrink-0"
                                style={{ color: SEC.t3, transform: isUserExpanded ? 'rotate(90deg)' : 'none' }}
                              />
                              <User className="h-3.5 w-3.5 flex-shrink-0" style={{ color: SEC.cyan }} />
                              <span className="text-xs font-mono font-medium" style={{ color: SEC.t1 }}>
                                {userId || 'System'}
                              </span>
                              <span className="text-[10px] font-medium" style={{ color: SEC.t3 }}>
                                {userFindings.length} finding{userFindings.length !== 1 ? 's' : ''}
                              </span>
                              <span
                                className="text-[10px] px-1.5 py-0.5 rounded font-bold uppercase"
                                style={{ background: `${sevColor}20`, color: sevColor }}
                              >
                                {highestSeverity}
                              </span>
                              {allDismissed && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: `${SEC.green}20`, color: SEC.green }}>
                                  All Safe
                                </span>
                              )}
                              {isQuarantined && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: `${SEC.red}20`, color: SEC.red }}>
                                  Quarantined
                                </span>
                              )}
                              {isAlreadyBlocked && !isQuarantined && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: `${SEC.amber}20`, color: SEC.amber }}>
                                  Blocked
                                </span>
                              )}
                              {isReleased && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: `${SEC.green}20`, color: SEC.green }}>
                                  Released
                                </span>
                              )}
                              {releaseFailed && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: `${SEC.red}20`, color: SEC.red }}>
                                  Release Failed
                                </span>
                              )}

                              {/* Per-user action buttons (right side) */}
                              <div className="ml-auto flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                                {/* Quarantine / Release buttons */}
                                {userId && isAlreadyBlocked && !isReleased && (
                                  confirmRelease === userId ? (
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-[10px]" style={{ color: SEC.amber }}>Release?</span>
                                      <button
                                        onClick={() => handleReleaseUser(userId)}
                                        disabled={isReleasing}
                                        className="text-[10px] px-2 py-0.5 rounded"
                                        style={{ color: SEC.green, border: `1px solid ${SEC.green}40` }}
                                      >
                                        {isReleasing ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Yes'}
                                      </button>
                                      <button
                                        onClick={() => setConfirmRelease(null)}
                                        className="text-[10px] px-2 py-0.5 rounded"
                                        style={{ color: SEC.t3, border: `1px solid ${SEC.border}` }}
                                      >
                                        No
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => setConfirmRelease(userId)}
                                      disabled={isReleasing || releasingAll}
                                      className="flex items-center gap-1 text-[10px] px-2 py-1 rounded transition-colors"
                                      style={{ color: SEC.green, border: `1px solid ${SEC.green}40`, background: `${SEC.green}10` }}
                                    >
                                      {isReleasing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Unlock className="h-3 w-3" />}
                                      Release
                                    </button>
                                  )
                                )}
                                {userId && !allDismissed && !isQuarantined && !isAlreadyBlocked && (
                                  confirmQuarantine === userId ? (
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-[10px]" style={{ color: SEC.amber }}>Block?</span>
                                      <button
                                        onClick={() => handleQuarantineUser(userId)}
                                        disabled={quarantiningUser === userId}
                                        className="text-[10px] px-2 py-0.5 rounded font-medium"
                                        style={{ color: SEC.red, border: `1px solid ${SEC.red}40`, background: `${SEC.red}10` }}
                                      >
                                        {quarantiningUser === userId ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Yes, Block'}
                                      </button>
                                      <button
                                        onClick={() => setConfirmQuarantine(null)}
                                        className="text-[10px] px-2 py-0.5 rounded"
                                        style={{ color: SEC.t3, border: `1px solid ${SEC.border}` }}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => setConfirmQuarantine(userId)}
                                      disabled={!!quarantiningUser}
                                      className="flex items-center gap-1 text-[10px] px-2 py-1 rounded transition-colors"
                                      style={{ color: SEC.red, border: `1px solid ${SEC.red}40`, background: `${SEC.red}08` }}
                                    >
                                      <ShieldBan className="h-3 w-3" />
                                      Quarantine
                                    </button>
                                  )
                                )}
                              </div>
                            </div>

                            {/* User expanded findings */}
                            {isUserExpanded && (
                              <div className="divide-y" style={{ borderColor: SEC.border, borderTop: `1px solid ${SEC.border}` }}>
                                {userFindings.map(f => (
                                  <FindingDetail
                                    key={f.id}
                                    finding={f}
                                    isDismissed={dismissedFindings.has(f.id)}
                                    isDismissing={dismissingFinding === f.id}
                                    isUndismissing={undismissingFinding === f.id}
                                    onDismiss={() => handleDismissFinding(f.id)}
                                    onUndismiss={() => handleUndismissFinding(f.id)}
                                    clientId={alert.clientId}
                                  />
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {findings && findings.length === 0 && (
                    <p className="text-xs" style={{ color: SEC.t4 }}>No matching findings found</p>
                  )}
                </div>
              )}

              {/* Notes */}
              {alert.notes && (
                <div>
                  <p className="text-[10px] font-medium uppercase tracking-wider mb-0.5" style={{ color: SEC.t3 }}>
                    Notes
                  </p>
                  <p className="text-xs" style={{ color: SEC.t2 }}>{alert.notes}</p>
                </div>
              )}

              {/* Actions — simplified */}
              {alert.status !== 'resolved' && alert.status !== 'false_positive' && (
                <div className="flex items-center gap-2 pt-2" style={{ borderTop: `1px solid ${SEC.border}` }}>
                  {actionLoading ? (
                    <div className="flex items-center gap-2 text-xs" style={{ color: SEC.t3 }}>
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Updating...
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={(e) => { e.stopPropagation(); onAction(alert.id, alert.clientId, 'false_positive'); }}
                        className="flex items-center gap-1 text-xs px-3 py-1.5 rounded transition-colors"
                        style={{ color: SEC.green, border: `1px solid ${SEC.green}40`, background: `${SEC.green}10` }}
                      >
                        <ShieldCheck className="h-3 w-3" />
                        All Safe — Dismiss Alert
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); onAction(alert.id, alert.clientId, 'resolved'); }}
                        className="flex items-center gap-1 text-xs px-3 py-1.5 rounded transition-colors"
                        style={{ color: SEC.t3, border: `1px solid ${SEC.border}` }}
                      >
                        <CheckCircle2 className="h-3 w-3" />
                        Resolve
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

interface FindingDetailProps {
  finding: SecurityFinding;
  isDismissed: boolean;
  isDismissing: boolean;
  isUndismissing: boolean;
  onDismiss: () => void;
  onUndismiss: () => void;
  clientId: string;
}

function FindingDetail({ finding, isDismissed, isDismissing, isUndismissing, onDismiss, onUndismiss, clientId }: FindingDetailProps) {
  const [disablingRule, setDisablingRule] = useState(false);
  const [ruleDisabled, setRuleDisabled] = useState(false);
  const [disableError, setDisableError] = useState<string | null>(null);

  const isSuspiciousRule = finding.type === 'suspicious_inbox_rule';
  let rawData: any = null;
  try {
    rawData = finding.rawData || (finding.details ? JSON.parse(finding.details) : null);
  } catch { /* details not JSON */ }

  const handleDisableRule = async () => {
    if (!rawData) return;
    setDisablingRule(true);
    setDisableError(null);
    try {
      await disableInboxRule({
        clientId,
        userId: finding.userId || '',
        ruleId: rawData.ruleId || undefined,
        ruleName: rawData.ruleName || undefined,
      });
      setRuleDisabled(true);
    } catch (err: any) {
      setDisableError(err.message || 'Failed to disable rule');
    } finally {
      setDisablingRule(false);
    }
  };

  const actionButtons = (
    <div className="flex items-center gap-1.5 ml-auto" onClick={e => e.stopPropagation()}>
      {isDismissed || finding.status === 'false_positive' ? (
        <button
          onClick={onUndismiss}
          disabled={isUndismissing}
          className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded transition-colors hover:bg-white/5 cursor-pointer"
          style={{ background: `${SEC.green}15`, color: SEC.green, border: `1px solid ${SEC.green}30` }}
          title="Click to undo"
        >
          {isUndismissing ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
          Safe
        </button>
      ) : (
        <button
          onClick={onDismiss}
          disabled={isDismissing}
          className="flex items-center gap-1 text-[10px] px-2 py-1 rounded transition-colors hover:bg-white/5"
          style={{ color: SEC.green, border: `1px solid ${SEC.green}30` }}
        >
          {isDismissing ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
          Mark as Safe
        </button>
      )}
      {isSuspiciousRule && rawData && !isDismissed && finding.status !== 'false_positive' && (
        ruleDisabled ? (
          <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded" style={{ background: `${SEC.red}15`, color: SEC.red }}>
            <XCircle className="h-3 w-3" />
            Disabled
          </span>
        ) : (
          <button
            onClick={handleDisableRule}
            disabled={disablingRule}
            className="flex items-center gap-1 text-[10px] px-2 py-1 rounded transition-colors hover:bg-white/5"
            style={{ color: SEC.red, border: `1px solid ${SEC.red}30` }}
          >
            {disablingRule ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />}
            Disable Inbox Rule
          </button>
        )
      )}
    </div>
  );

  if (isSuspiciousRule && rawData) {
    const ruleName = rawData.ruleName || rawData.rule?.Name || '';
    const reasons: string[] = rawData.reasons || [];
    const hasAnyAction = rawData.forwardTo || rawData.redirectTo || rawData.forwardAsAttachmentTo ||
      rawData.moveToFolder || rawData.deleteMessage || rawData.markAsRead;
    const subjectWords = rawData.subjectContainsWords || [];
    const fromWords = rawData.fromAddressContainsWords || [];
    const bodyWords = rawData.bodyContainsWords || [];

    return (
      <div
        className="px-3 py-2 space-y-1.5"
        style={{
          background: isDismissed ? `${SEC.green}05` : SEC.bg,
          opacity: isDismissed ? 0.6 : 1,
        }}
      >
        <div className="flex items-center gap-2">
          <span
            className="text-[10px] px-1.5 py-0.5 rounded font-bold"
            style={{ background: `${severityColor(finding.severity)}20`, color: severityColor(finding.severity) }}
          >
            {finding.severity}
          </span>
          <span className="text-[11px] font-medium" style={{ color: SEC.t2 }}>Suspicious Inbox Rule</span>
          {rawData.enabled === false && (
            <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: `${SEC.t3}20`, color: SEC.t3 }}>
              disabled
            </span>
          )}
          {actionButtons}
        </div>
        <div className="flex items-center gap-1.5">
          <BookOpen className="h-3 w-3" style={{ color: SEC.t3 }} />
          <code className="text-xs px-1.5 py-0.5 rounded" style={{ background: SEC.panel, color: SEC.cyan }}>
            {ruleName || '(unnamed)'}
          </code>
          <span className="text-[10px]" style={{ color: SEC.t4 }}>
            Priority: {rawData.priority ?? '?'}
          </span>
        </div>

        {/* Conditions (what triggers this rule) */}
        {(subjectWords.length > 0 || fromWords.length > 0 || bodyWords.length > 0) && (
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wider mb-0.5" style={{ color: SEC.t4 }}>
              When:
            </p>
            <div className="flex flex-wrap gap-1.5 text-[11px]">
              {subjectWords.length > 0 && (
                <span className="px-1.5 py-0.5 rounded" style={{ background: SEC.panel, color: SEC.t2 }}>
                  Subject contains: {subjectWords.join(', ')}
                </span>
              )}
              {fromWords.length > 0 && (
                <span className="px-1.5 py-0.5 rounded" style={{ background: SEC.panel, color: SEC.t2 }}>
                  From contains: {fromWords.join(', ')}
                </span>
              )}
              {bodyWords.length > 0 && (
                <span className="px-1.5 py-0.5 rounded" style={{ background: SEC.panel, color: SEC.t2 }}>
                  Body contains: {bodyWords.join(', ')}
                </span>
              )}
            </div>
          </div>
        )}
        {subjectWords.length === 0 && fromWords.length === 0 && bodyWords.length === 0 && (
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wider mb-0.5" style={{ color: SEC.t4 }}>
              When:
            </p>
            <span className="text-[11px] px-1.5 py-0.5 rounded" style={{ background: `${SEC.amber}10`, color: SEC.amber }}>
              No conditions — applies to ALL incoming mail
            </span>
          </div>
        )}

        {/* Actions (what the rule does) */}
        <div>
          <p className="text-[10px] font-medium uppercase tracking-wider mb-0.5" style={{ color: SEC.t4 }}>
            Then:
          </p>
          <div className="flex flex-wrap gap-2 text-[11px]">
            {rawData.forwardTo && (
              <span className="flex items-center gap-1 px-1.5 py-0.5 rounded" style={{ background: `${SEC.red}15`, color: SEC.red }}>
                <Mail className="h-2.5 w-2.5" /> Forward to: {rawData.forwardTo}
              </span>
            )}
            {rawData.forwardAsAttachmentTo && (
              <span className="flex items-center gap-1 px-1.5 py-0.5 rounded" style={{ background: `${SEC.red}15`, color: SEC.red }}>
                <Mail className="h-2.5 w-2.5" /> Forward as attachment to: {rawData.forwardAsAttachmentTo}
              </span>
            )}
            {rawData.redirectTo && (
              <span className="flex items-center gap-1 px-1.5 py-0.5 rounded" style={{ background: `${SEC.red}15`, color: SEC.red }}>
                <ArrowRight className="h-2.5 w-2.5" /> Redirect to: {rawData.redirectTo}
              </span>
            )}
            {rawData.moveToFolder && (
              <span className="flex items-center gap-1 px-1.5 py-0.5 rounded" style={{ background: `${SEC.amber}15`, color: SEC.amber }}>
                <FolderOpen className="h-2.5 w-2.5" /> Move to: {rawData.moveToFolder}
              </span>
            )}
            {rawData.deleteMessage && (
              <span className="flex items-center gap-1 px-1.5 py-0.5 rounded" style={{ background: `${SEC.red}15`, color: SEC.red }}>
                <Trash2 className="h-2.5 w-2.5" /> Delete message
              </span>
            )}
            {rawData.markAsRead && (
              <span className="flex items-center gap-1 px-1.5 py-0.5 rounded" style={{ background: `${SEC.amber}15`, color: SEC.amber }}>
                <Eye className="h-2.5 w-2.5" /> Mark as read
              </span>
            )}
            {rawData.stopProcessingRules && (
              <span className="text-[11px] px-1.5 py-0.5 rounded" style={{ background: SEC.panel, color: SEC.t3 }}>
                Stop processing more rules
              </span>
            )}
            {!hasAnyAction && !rawData.stopProcessingRules && (
              <span className="text-[11px] px-1.5 py-0.5 rounded" style={{ background: SEC.panel, color: SEC.t4 }}>
                No visible actions — rule may only have a suspicious name
              </span>
            )}
          </div>
        </div>

        {/* Why flagged */}
        {reasons.length > 0 && (
          <div className="mt-1">
            <p className="text-[10px] font-medium uppercase tracking-wider mb-0.5" style={{ color: SEC.t4 }}>Why flagged:</p>
            <div className="space-y-0.5">
              {reasons.map((r, i) => (
                <div key={i} className="flex items-start gap-1.5 text-[11px]" style={{ color: SEC.t2 }}>
                  <AlertTriangle className="h-2.5 w-2.5 mt-0.5 flex-shrink-0" style={{ color: SEC.amber }} />
                  {r}
                </div>
              ))}
            </div>
          </div>
        )}
        {disableError && (
          <p className="text-[10px] mt-1" style={{ color: SEC.red }}>{disableError}</p>
        )}
      </div>
    );
  }

  // Generic finding display for other types
  return (
    <div
      className="px-3 py-2 space-y-1"
      style={{
        background: isDismissed ? `${SEC.green}05` : SEC.bg,
        opacity: isDismissed ? 0.6 : 1,
      }}
    >
      <div className="flex items-center gap-2">
        <span
          className="text-[10px] px-1.5 py-0.5 rounded font-bold"
          style={{ background: `${severityColor(finding.severity)}20`, color: severityColor(finding.severity) }}
        >
          {finding.severity}
        </span>
        <span className="text-[11px] font-medium" style={{ color: SEC.t2 }}>
          {iocTypeLabel(finding.type)}
        </span>
        {actionButtons}
      </div>
      <p className="text-sm leading-relaxed" style={{ color: SEC.t2 }}>
        {finding.summary}
      </p>
      {finding.details && finding.details !== finding.summary && (
        <p className="text-[11px] leading-relaxed" style={{ color: SEC.t4 }}>
          {finding.details.substring(0, 300)}{finding.details.length > 300 ? '...' : ''}
        </p>
      )}
    </div>
  );
}

function groupFindingsByUser(findings: SecurityFinding[]): [string, SecurityFinding[]][] {
  const groups = new Map<string, SecurityFinding[]>();
  for (const f of findings) {
    const key = f.userId || '';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(f);
  }
  return Array.from(groups.entries());
}

function formatDate(dateStr: string): string {
  try {
    return format(parseISO(dateStr), 'MMM d HH:mm');
  } catch {
    return dateStr;
  }
}
